import { useMemo, useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import Input from '@cartwhl/ui/atoms/Input';
import Text from '@cartwhl/ui/base/Text';
import ChevronRightIcon from '@cartwhl/ui/icons/ChevronRightIcon';
import CrossIcon from '@cartwhl/ui/icons/CrossIcon';
import PlusIcon from '@cartwhl/ui/icons/PlusIcon';

import {
  ROLE_MENU_GROUPS,
  isIterativeRole,
} from '../../../lib/schema/navigation';
import { ROLE_NAMES } from '../../../lib/schema/mapping';
import RoleTag from './RoleTag';

const BUILT_IN_ROLE_NAMES = new Set<string>(ROLE_NAMES);

interface RoleGroupSubmenuProps {
  label: string;
  roles: readonly string[];
  selectedRole: string | null;
  assignedRoleNames: ReadonlySet<string>;
  onRoleChange: (nextRole: string) => void;
}

interface SideSplitRoleGroupSubmenuProps extends RoleGroupSubmenuProps {}

interface RoleOverrideControlProps {
  role: string | null;
  confidence: number | null;
  presentRoleNames: ReadonlySet<string>;
  onRoleChange: (nextRole: string | null) => void;
}

const SIDE_SPLIT_GROUP_KEYS = new Set(['arms', 'legs', 'fingers']);

export default function RoleOverrideControl({
  role,
  confidence,
  presentRoleNames,
  onRoleChange,
}: RoleOverrideControlProps) {
  const [customRoleName, setCustomRoleName] = useState('');
  const trimmedCustomRoleName = customRoleName.trim();

  const visibleRoleGroups = useMemo(
    () =>
      ROLE_MENU_GROUPS.map((group) => ({
        key: group.key,
        label: group.label,
        roles: group.roles.filter((roleName) => {
          if (!isIterativeRole(roleName)) return true;
          return presentRoleNames.has(roleName);
        }),
      })).filter((group) => group.roles.length > 0),
    [presentRoleNames],
  );

  const presentOtherRoleNames = useMemo(() => {
    const otherRoleNames = Array.from(presentRoleNames).filter(
      (roleName) => !BUILT_IN_ROLE_NAMES.has(roleName),
    );
    if (
      role &&
      !BUILT_IN_ROLE_NAMES.has(role) &&
      !otherRoleNames.includes(role)
    ) {
      otherRoleNames.push(role);
    }
    return otherRoleNames.sort((a, b) => a.localeCompare(b));
  }, [presentRoleNames, role]);

  const stopPropagation = (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
  };

  const assignCustomRole = () => {
    if (!trimmedCustomRoleName) return;
    onRoleChange(trimmedCustomRoleName);
    setCustomRoleName('');
  };

  return (
    <Dropdown
      inModal
      align="start"
      side="bottom"
      width={288}
      onOpenChange={(open) => {
        if (!open) setCustomRoleName('');
      }}
      trigger={
        <button
          type="button"
          className="group dark:hover:bg-gray-90/40 hover:bg-gray-15/70 flex w-full min-w-0 rounded-md px-1 py-0.5 text-left transition-colors"
          onPointerDown={stopPropagation}
          onClick={stopPropagation}
          aria-label={role ? `Role ${role}. Click to update` : 'Assign role'}
        >
          <span className="min-w-0 flex-1">
            {role ? (
              <RoleTag role={role} confidence={confidence} />
            ) : (
              <span className="flex h-4 items-center justify-center">
                <PlusIcon className="text-gray-60 dark:text-gray-45 h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-100" />
              </span>
            )}
          </span>
        </button>
      }
    >
      <Dropdown.Item
        onClick={() => onRoleChange(null)}
        disabled={!role}
        icon={<CrossIcon className="text-gray-60 dark:text-gray-45 h-3.5 w-3.5" />}
      >
        Clear role
      </Dropdown.Item>
      <Dropdown.Separator />
      {visibleRoleGroups.map((group) => (
        SIDE_SPLIT_GROUP_KEYS.has(group.key) ? (
          <SideSplitRoleGroupSubmenu
            key={group.key}
            label={group.label}
            roles={group.roles}
            selectedRole={role}
            assignedRoleNames={presentRoleNames}
            onRoleChange={onRoleChange}
          />
        ) : (
          <RoleGroupSubmenu
            key={group.key}
            label={group.label}
            roles={group.roles}
            selectedRole={role}
            assignedRoleNames={presentRoleNames}
            onRoleChange={onRoleChange}
          />
        )
      ))}

      {presentOtherRoleNames.length > 0 && (
        <RoleGroupSubmenu
          label="Other"
          roles={presentOtherRoleNames}
          selectedRole={role}
          assignedRoleNames={presentRoleNames}
          onRoleChange={onRoleChange}
        />
      )}
      <Dropdown.Separator />
      <div className="px-2 pt-1 pb-1">
        <Input
          name="custom-role-name"
          value={customRoleName}
          onChange={(event) => setCustomRoleName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            assignCustomRole();
          }}
          placeholder="Custom Name"
          className="w-full"
        />
      </div>
    </Dropdown>
  );
}

function RoleGroupSubmenu({
  label,
  roles,
  selectedRole,
  assignedRoleNames,
  onRoleChange,
}: RoleGroupSubmenuProps) {
  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger className="dark:hover:bg-gray-90 hover:bg-gray-15 data-[state=open]:bg-gray-15 dark:data-[state=open]:bg-gray-90 outline-hidden flex w-full items-center rounded-lg py-2.5 pl-3 pr-3 transition-colors">
        <Text variant="studio/body/medium/sm">{label}</Text>
        <ChevronRightIcon className="text-gray-45 ml-auto" />
      </DropdownMenu.SubTrigger>
      <DropdownMenu.Portal>
        <DropdownMenu.SubContent
          sideOffset={8}
          alignOffset={-8}
          collisionPadding={8}
          className="RxSubContent text-gray-90 dark:bg-gray-75 shadow-menu z-20 min-w-[224px] rounded-2xl bg-white p-2 dark:text-white"
        >
          <Text as="div" variant="studio/body/medium/sm">
            <div className="max-h-64 overflow-y-auto">
              {roles.map((nextRole) => (
                <RoleOptionItem
                  key={nextRole}
                  roleName={nextRole}
                  selectedRole={selectedRole}
                  assignedRoleNames={assignedRoleNames}
                  onRoleChange={onRoleChange}
                />
              ))}
            </div>
          </Text>
        </DropdownMenu.SubContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Sub>
  );
}

function SideSplitRoleGroupSubmenu({
  label,
  roles,
  selectedRole,
  assignedRoleNames,
  onRoleChange,
}: SideSplitRoleGroupSubmenuProps) {
  const leftRoles = roles.filter((roleName) => roleName.startsWith('Left'));
  const rightRoles = roles.filter((roleName) => roleName.startsWith('Right'));
  const centerRoles = roles.filter(
    (roleName) =>
      !roleName.startsWith('Left') && !roleName.startsWith('Right'),
  );

  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger className="dark:hover:bg-gray-90 hover:bg-gray-15 data-[state=open]:bg-gray-15 dark:data-[state=open]:bg-gray-90 outline-hidden flex w-full items-center rounded-lg py-2.5 pl-3 pr-3 transition-colors">
        <Text variant="studio/body/medium/sm">{label}</Text>
        <ChevronRightIcon className="text-gray-45 ml-auto" />
      </DropdownMenu.SubTrigger>
      <DropdownMenu.Portal>
        <DropdownMenu.SubContent
          sideOffset={8}
          alignOffset={-8}
          collisionPadding={8}
          className="RxSubContent text-gray-90 dark:bg-gray-75 shadow-menu z-20 min-w-[224px] rounded-2xl bg-white p-2 dark:text-white"
        >
          {leftRoles.length > 0 && (
            <RoleGroupSubmenu
              label="Left"
              roles={leftRoles}
              selectedRole={selectedRole}
              assignedRoleNames={assignedRoleNames}
              onRoleChange={onRoleChange}
            />
          )}
          {rightRoles.length > 0 && (
            <RoleGroupSubmenu
              label="Right"
              roles={rightRoles}
              selectedRole={selectedRole}
              assignedRoleNames={assignedRoleNames}
              onRoleChange={onRoleChange}
            />
          )}
          {centerRoles.length > 0 && (
            <div className="max-h-64 overflow-y-auto">
              {centerRoles.map((nextRole) => (
                <RoleOptionItem
                  key={nextRole}
                  roleName={nextRole}
                  selectedRole={selectedRole}
                  assignedRoleNames={assignedRoleNames}
                  onRoleChange={onRoleChange}
                />
              ))}
            </div>
          )}
        </DropdownMenu.SubContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Sub>
  );
}

interface RoleOptionItemProps {
  roleName: string;
  selectedRole: string | null;
  assignedRoleNames: ReadonlySet<string>;
  onRoleChange: (nextRole: string) => void;
}

function RoleOptionItem({
  roleName,
  selectedRole,
  assignedRoleNames,
  onRoleChange,
}: RoleOptionItemProps) {
  const isActive = roleName === selectedRole;
  const isAssigned = assignedRoleNames.has(roleName);

  return (
    <Dropdown.Item
      onClick={() => onRoleChange(roleName)}
      className={isActive ? 'bg-blue hover:bg-blue text-white' : undefined}
      icon={
        <span
          className={`mt-0.5 inline-flex h-2 w-2 rounded-full ${
            isAssigned
              ? isActive
                ? 'bg-white'
                : 'bg-blue-50'
              : 'bg-transparent'
          }`}
          aria-hidden
        />
      }
    >
      {roleName}
    </Dropdown.Item>
  );
}
